/** 參考 iNoBounce 並改寫 */
/*! iNoBounce - v0.1.6
 * https://github.com/lazd/iNoBounce/
 * Copyright (c) 2013 Larry Davis <lazdnet@gmail.com> Licensed BSD */

import { useEventListener } from '@vueuse/core';
import { readonly, ref } from 'vue';

/**
 * 停止事件監聽器的函式型態
 */
type StopListenerFunction = (() => void) | null;

/**
 * Touch 或 Mouse 事件介面
 */
interface TouchOrMouseEvent {
  target: EventTarget | null;
  touches?: TouchList;
  screenY: number;
  preventDefault(): void;
}

export const useScrollLocker = () => {
  // Stores the Y position where the touch started
  let startY = 0;

  // Store enable status
  const isEnable = ref(false);

  let touchstartListenerStop: StopListenerFunction = null;
  let touchmoveListenerStop: StopListenerFunction = null;

  let supportsPassiveOption = false;
  try {
    const opts = Object.defineProperty({}, 'passive', {
      get: () => {
        supportsPassiveOption = true;
        return undefined;
      },
    });
    // 保持原始 iNoBounce 的檢測邏輯，只是加上必要的型態轉換
    window.addEventListener('test' as string, null as unknown as EventListener, opts);
  } catch (e) {
    // passive option 檢測失敗
  }

  const handleTouchmove = (evt: TouchOrMouseEvent) => {
    // Get the element that was scrolled upon
    let el = evt.target as Element | null;

    // Allow zooming
    const zoom = window.innerWidth / window.document.documentElement.clientWidth;
    if (evt.touches && evt.touches.length > 1 || zoom !== 1) {
      return;
    }

    // Check all parent elements for scrollability
    while (el && el !== document.body && el !== document.documentElement) {
      // Get some style properties
      const style = window.getComputedStyle(el);

      if (!style) {
        // If we've encountered an element we can't compute the style for, get out
        break;
      }

      // Ignore range input element
      if (el.nodeName === 'INPUT' && el.getAttribute('type') === 'range') {
        return;
      }

      const scrolling = style.getPropertyValue('-webkit-overflow-scrolling');
      const overflowY = style.getPropertyValue('overflow-y');
      const height = parseInt(style.getPropertyValue('height'), 10);

      // Determine if the element should scroll
      const isScrollable =
        scrolling === 'touch' && (overflowY === 'auto' || overflowY === 'scroll');
      const canScroll = (el as HTMLElement).scrollHeight > (el as HTMLElement).offsetHeight;

      if (isScrollable && canScroll) {
        // Get the current Y position of the touch
        const curY = evt.touches ? evt.touches[0].screenY : evt.screenY;

        // Determine if the user is trying to scroll past the top or bottom
        // In this case, the window will bounce, so we have to prevent scrolling completely
        const isAtTop = startY <= curY && (el as HTMLElement).scrollTop === 0;
        const isAtBottom =
          startY >= curY && (el as HTMLElement).scrollHeight - (el as HTMLElement).scrollTop === height;

        // Stop a bounce bug when at the bottom or top of the scrollable element
        if (isAtTop || isAtBottom) {
          evt.preventDefault();
        }

        // No need to continue up the DOM, we've done our job
        return;
      }

      // Test the next parent
      el = el.parentElement;
    }

    // Stop the bouncing -- no parents are scrollable
    evt.preventDefault();
  };

  const handleTouchstart = (evt: TouchOrMouseEvent) => {
    // Store the first Y position of the touch
    startY = evt.touches ? evt.touches[0].screenY : evt.screenY;
  };

  const enable = () => {
    if (isEnable.value) {
      return;
    }

    // Listen to a couple key touch events
    touchstartListenerStop = useEventListener(
      window,
      'touchstart',
      handleTouchstart,
      supportsPassiveOption ? { passive: false } : false,
    );
    touchmoveListenerStop = useEventListener(
      window,
      'touchmove',
      handleTouchmove,
      supportsPassiveOption ? { passive: false } : false,
    );

    isEnable.value = true;
  };

  const disable = () => {
    if (!isEnable.value) {
      return;
    }

    // Stop listening
    touchstartListenerStop?.();
    touchmoveListenerStop?.();
    touchstartListenerStop = null;
    touchmoveListenerStop = null;
    isEnable.value = false;
  };

  return {
    enable,
    disable,
    isEnable: readonly(isEnable),
  };
};
